import React, { useState, useRef, useEffect } from 'react';

const API_BASE = 'https://surabaya-api.onrender.com/api';

// ============================================
// MODULE JOURNEE GROUPE A LA CARTE — constructeur back-office
// Admin uniquement. Aucun parcours client. Une journee = contact + date + N segments.
// ============================================

const COLORS = {
  brun: '#4A2C17', brunLight: '#6B3F24', or: '#E8C47C', orLight: '#F2D89A',
  cream: '#FDF6EC', gris: '#8C7B6E', grisClair: '#F5EDE3', blanc: '#FFFFFF',
  rouge: '#C0392B', vert: '#27AE60', bleu: '#2980B9',
};

// Les 4 briques d'activite
const ACTIVITIES = {
  accueil_degustation: { label: 'Accueil / dégustation', icon: '🥂', defStart: '09:00', defEnd: '09:45' },
  caleche:             { label: 'Calèche',                icon: '🐴', defStart: '10:00', defEnd: '11:30' },
  repas:               { label: 'Repas',                  icon: '🍽️', defStart: '12:00', defEnd: '14:00' },
  bateau:              { label: 'Bateau',                 icon: '⛵', defStart: '15:00', defEnd: '15:45' },
};
const ACTIVITY_ORDER = ['accueil_degustation', 'caleche', 'repas', 'bateau'];

const DESTINATIONS = { belvedere: 'Belvédère', saut_du_doubs: 'Saut du Doubs' };
const PRESTATAIRES = {
  nlb_les_brenets: 'NLB — Les Brenets (CH)',
  vedettes_panoramiques: 'Vedettes Panoramiques (FR)',
};
const STATUS_OPTS = { option: 'Option', confirme: 'Confirmé', annule: 'Annulé' };

// ── Tarification (v2) ──────────────────────────────────────────────
// Le catalogue vient du backend (group_pricing_catalog, versionné par
// saison). Aucun prix n'est codé en dur ici.
const MENU_OPTS = { menu1: 'Menu 1', menu23: 'Menu 2 ou 3', menu4: 'Menu 4' };

// Reference de dossier, derivee du seul id donc immuable.
// Miroir de referenceDossier() dans services/groupPricing.js.
function referenceDossier(id) {
  const n = parseInt(id, 10);
  return n > 0 ? 'SUR-' + String(n).padStart(4, '0') : '';
}

function fmtEur(n) {
  const v = Math.round((parseFloat(n) || 0) * 100) / 100;
  const abs = Math.abs(v).toFixed(2).replace('.', ',').split(',');
  abs[0] = abs[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return (v < 0 ? '−' : '') + abs.join(',') + ' €';
}

// "2 640,50" / "2640.5" -> 2640.5
function parseEur(txt) {
  const n = parseFloat(String(txt).replace(/[\s\u00A0€]/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

const s = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(74,44,23,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '24px', overflowY: 'auto' },
  box: { backgroundColor: COLORS.blanc, borderRadius: '16px', padding: '28px 32px', width: '100%', maxWidth: '720px', boxShadow: '0 20px 60px rgba(74,44,23,0.4)', fontFamily: "'Georgia', 'Times New Roman', serif", color: COLORS.brun, margin: 'auto' },
  title: { fontSize: '21px', fontWeight: 'bold', color: COLORS.brun, marginBottom: '4px' },
  sub: { fontSize: '13px', color: COLORS.gris, fontStyle: 'italic', marginBottom: '20px' },
  sectionTitle: { fontSize: '12px', fontWeight: 'bold', color: COLORS.gris, textTransform: 'uppercase', letterSpacing: '1px', padding: '8px 0 8px', borderBottom: `2px solid ${COLORS.or}`, marginBottom: '14px', marginTop: '24px' },
  label: { display: 'block', fontSize: '11px', fontWeight: 'bold', color: COLORS.gris, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '5px' },
  input: { width: '100%', padding: '9px 12px', border: `1px solid ${COLORS.grisClair}`, borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', color: COLORS.brun, backgroundColor: COLORS.cream, boxSizing: 'border-box', outline: 'none' },
  group: { marginBottom: '14px' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  row3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' },
  segCard: { border: `1px solid ${COLORS.grisClair}`, borderRadius: '12px', padding: '14px 16px', marginBottom: '12px', backgroundColor: '#FFFDF8' },
  segHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' },
  segTitle: { fontSize: '15px', fontWeight: 'bold', color: COLORS.brun, display: 'flex', alignItems: 'center', gap: '8px' },
  iconBtn: { background: COLORS.grisClair, border: `1px solid ${COLORS.or}`, color: COLORS.brun, width: '30px', height: '30px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  addBar: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px', marginBottom: '8px' },
  addBtn: { backgroundColor: COLORS.cream, color: COLORS.brun, border: `1.5px dashed ${COLORS.or}`, padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit', fontWeight: 'bold' },
  recap: { backgroundColor: COLORS.grisClair, border: `1px solid ${COLORS.or}`, borderRadius: '10px', padding: '14px 18px', fontSize: '14px', whiteSpace: 'pre-wrap', lineHeight: 1.5, color: COLORS.brunLight, fontFamily: 'inherit' },
  actions: { display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' },
  btnPrimary: { backgroundColor: COLORS.or, color: COLORS.brun, border: 'none', padding: '11px 22px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', fontFamily: 'inherit' },
  btnSecondary: { backgroundColor: 'transparent', color: COLORS.brun, border: `2px solid ${COLORS.brun}`, padding: '9px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit' },
  btnGhost: { backgroundColor: 'transparent', color: COLORS.bleu, border: `1.5px solid ${COLORS.bleu}`, padding: '9px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit', textDecoration: 'none', display: 'inline-block' },
  tarifGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' },
  tarifTable: { border: `1px solid ${COLORS.grisClair}`, borderRadius: '10px', overflow: 'hidden', marginTop: '14px' },
  tarifRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', borderBottom: `1px solid ${COLORS.grisClair}`, fontSize: '13.5px' },
  tarifDetail: { fontSize: '11.5px', color: COLORS.gris, whiteSpace: 'nowrap' },
  tarifAmount: { minWidth: '104px', textAlign: 'right', fontWeight: 'bold', cursor: 'pointer', padding: '3px 6px', borderRadius: '5px', border: '1px solid transparent', fontVariantNumeric: 'tabular-nums' },
  tarifInput: { width: '104px', textAlign: 'right', padding: '3px 6px', borderRadius: '5px', border: `1px solid ${COLORS.or}`, fontSize: '13.5px', fontFamily: 'inherit', color: COLORS.brun, backgroundColor: COLORS.blanc },
  tarifFoot: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '28px', padding: '14px 12px', backgroundColor: COLORS.grisClair, flexWrap: 'wrap' },
  tarifBig: { fontSize: '22px', fontWeight: 'bold', color: COLORS.brun, fontVariantNumeric: 'tabular-nums' },
  tarifBigLabel: { fontSize: '10.5px', color: COLORS.gris, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' },
  banner: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', backgroundColor: '#FCEFC7', border: `1px solid ${COLORS.or}`, borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#7B5E00', marginBottom: '12px' },
  bannerLock: { backgroundColor: '#EAF3FB', border: `1px solid ${COLORS.bleu}`, color: '#1B4F72' },
  miniBtn: { backgroundColor: COLORS.blanc, border: `1px solid ${COLORS.or}`, color: COLORS.brun, padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit', fontWeight: 'bold' },
  hint: { fontSize: '11.5px', color: COLORS.gris, fontStyle: 'italic', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  mailRow: { border: `1px solid ${COLORS.grisClair}`, borderRadius: '10px', padding: '10px 14px', marginBottom: '8px', backgroundColor: '#FFFDF8', cursor: 'pointer' },
  mailHead: { display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' },
  mailSubject: { fontWeight: 'bold', fontSize: '14px', color: COLORS.brun, flex: 1, minWidth: '160px' },
  mailMeta: { fontSize: '11.5px', color: COLORS.gris, whiteSpace: 'nowrap' },
  mailSnippet: { fontSize: '12.5px', color: COLORS.gris, marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
  bulle: { border: `1px solid ${COLORS.grisClair}`, borderRadius: '10px', padding: '10px 12px', marginBottom: '8px', backgroundColor: COLORS.blanc },
  bulleNous: { backgroundColor: '#F3F8F4', borderColor: '#CFE3D6' },
  bulleHead: { display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '11.5px', color: COLORS.gris, marginBottom: '6px', flexWrap: 'wrap' },
  bulleBody: { fontSize: '13px', color: COLORS.brun, whiteSpace: 'pre-wrap', lineHeight: 1.5, maxHeight: '320px', overflowY: 'auto' },
  errorBox: { backgroundColor: '#FFF0F0', border: '1px solid #FFCCCC', borderRadius: '8px', padding: '10px 14px', color: COLORS.rouge, fontSize: '14px', marginBottom: '14px' },
};

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function hhmm(t) { return t ? String(t).slice(0, 5) : ''; }

// segment vierge selon le type
// Tag affiché pour un sous-groupe (0 = commun)
function subGroupTag(sg, nbGroups) {
  if (nbGroups <= 1) return '';
  return (sg >= 1 ? `[G${sg}] ` : '[Commun] ');
}

function blankSegment(type) {
  const a = ACTIVITIES[type];
  const base = { activity_type: type, start_time: a.defStart, end_time: a.defEnd, sub_group: 0, details: {} };
  if (type === 'caleche') base.details = { destination: 'belvedere' };
  if (type === 'bateau') base.details = { prestataire: 'nlb_les_brenets' };
  if (type === 'repas') base.details = { entree: '', plat: '', dessert: '', couverts: '', exceptions: '' };
  if (type === 'accueil_degustation') base.details = { remarque: '' };
  return base;
}

// Construit le recap texte de la journee
function buildRecap(segments, nbGroups) {
  if (!segments.length) return 'Aucune activité pour le moment.';
  return segments.map(seg => {
    const a = ACTIVITIES[seg.activity_type];
    const d = seg.details || {};
    let h = hhmm(seg.start_time) || '--:--';
    if (seg.end_time) h += '–' + hhmm(seg.end_time);
    let line = `${h}  ${subGroupTag(seg.sub_group || 0, nbGroups)}${a.icon} ${a.label}`;
    if (seg.activity_type === 'caleche' && d.destination) line += ` → ${DESTINATIONS[d.destination] || d.destination}`;
    if (seg.activity_type === 'bateau' && d.prestataire) line += ` → ${PRESTATAIRES[d.prestataire] || d.prestataire}`;
    if (seg.activity_type === 'accueil_degustation' && d.remarque) line += `\n      ${d.remarque}`;
    if (seg.activity_type === 'repas') {
      const parts = [];
      if (d.entree) parts.push('Entrée : ' + d.entree);
      if (d.plat) parts.push('Plat : ' + d.plat);
      if (d.dessert) parts.push('Dessert : ' + d.dessert);
      if (d.couverts) parts.push('Couverts : ' + d.couverts);
      if (d.exceptions) parts.push('Exceptions : ' + d.exceptions);
      if (parts.length) line += '\n      ' + parts.join('\n      ');
    }
    return line;
  }).join('\n');
}

export default function GroupBookingModal({ onClose, onSave, onDelete, initialData, initialDate, token }) {
  const isEdit = !!(initialData && initialData.id);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [form, setForm] = useState({
    tour_operator: initialData?.tour_operator || '',
    contact_name: initialData?.contact_name || '',
    contact_email: initialData?.contact_email || '',
    contact_phone: initialData?.contact_phone || '',
    booking_date: initialData?.booking_date ? String(initialData.booking_date).slice(0, 10) : (initialDate || today()),
    pax_count: initialData?.pax_count || 10,
    status: initialData?.status || 'option',
    notes: initialData?.notes || '',
    nb_groups: initialData?.nb_groups || 1,
    // ── Tarif (v2). Reprise de l'existant : rien n'est inventé, une résa
    // sans tarif garde formula_code = null et le bloc s'affiche vide.
    formula_code: initialData?.formula_code || '',
    menu_choice: initialData?.menu_choice || '',
    drink_package: initialData?.drink_package || '',
    options: Array.isArray(initialData?.options) ? initialData.options : [],
    pax_free: initialData?.pax_free === undefined || initialData?.pax_free === null ? 2 : initialData.pax_free,
    discount_pct: initialData?.discount_pct || 0,
    bateau_non_facture: !!initialData?.bateau_non_facture,
    pricing_locked: !!initialData?.pricing_locked,
  });
  const [segments, setSegments] = useState(
    (initialData?.segments || []).map(seg => ({
      activity_type: seg.activity_type,
      start_time: hhmm(seg.start_time),
      end_time: hhmm(seg.end_time),
      sub_group: seg.sub_group || 0,
      details: seg.details || {},
    }))
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── Etat tarif ──────────────────────────────────────────────────
  // catalog === null : pas encore chargé. [] : chargé mais vide.
  const [catalog, setCatalog] = useState(null);
  const [catalogErr, setCatalogErr] = useState('');
  const [priceLines, setPriceLines] = useState(
    Array.isArray(initialData?.price_lines) ? initialData.price_lines : []
  );
  // Les overrides pilotent le recalcul : une ligne modifiée à la main
  // n'est jamais écrasée tant qu'on ne clique pas sur ↺.
  const [overrides, setOverrides] = useState(() => {
    const m = {};
    (Array.isArray(initialData?.price_lines) ? initialData.price_lines : [])
      .forEach(l => { if (l && l.key && l.override) m[l.key] = l.override; });
    return m;
  });
  const [totals, setTotals] = useState({
    unit_price: initialData?.unit_price || 0,
    total_price: initialData?.total_price || 0,
    invoice_text: initialData?.invoice_text || '',
    couverts_suggeres: 0,
  });
  const [fidelity, setFidelity] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [copied, setCopied] = useState(false);

  const season = parseInt(String(form.booking_date || '').slice(0, 4), 10) || new Date().getFullYear();

  // ── Chargement du catalogue tarifaire de la saison ──────────────
  useEffect(() => {
    let alive = true;
    setCatalogErr('');
    fetch(`${API_BASE}/admin/group-pricing?season=${season}`, {
      headers: token ? { Authorization: 'Bearer ' + token } : {},
    })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
      .then(d => { if (alive) setCatalog(d.items || []); })
      .catch(() => {
        if (!alive) return;
        setCatalog([]);
        setCatalogErr(`Catalogue tarifaire ${season} indisponible — la migration SQL est-elle passée ?`);
      });
    return () => { alive = false; };
  }, [season, token]);

  // Corps de la requête de calcul. Sert aussi de clé de dépendance :
  // tant que la chaîne ne change pas, on ne rappelle pas le serveur.
  const computeBody = JSON.stringify({
    tour_operator: form.tour_operator,
    booking_date: form.booking_date,
    season,
    formula_code: form.formula_code || null,
    menu_choice: form.menu_choice || null,
    drink_package: form.drink_package || null,
    options: form.options,
    pax_count: parseInt(form.pax_count, 10) || 0,
    pax_free: parseInt(form.pax_free, 10) || 0,
    discount_pct: parseFloat(form.discount_pct) || 0,
    bateau_non_facture: !!form.bateau_non_facture,
    pricing_locked: !!form.pricing_locked,
    // Verrouillé : on renvoie les lignes telles quelles (le serveur ne
    // recalcule pas). Sinon on ne transmet que les overrides.
    price_lines: form.pricing_locked
      ? priceLines
      : Object.keys(overrides).map(k => ({ key: k, override: overrides[k] })),
  });

  // ── Recalcul serveur (source unique de vérité), débounce 250 ms ──
  useEffect(() => {
    if (catalog === null) return undefined;
    let alive = true;
    const t = setTimeout(() => {
      fetch(`${API_BASE}/admin/group-pricing/compute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
        body: computeBody,
      })
        .then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
        .then(d => {
          if (!alive) return;
          setPriceLines(d.lines || []);
          setTotals({
            unit_price: d.unit_price || 0,
            total_price: d.total_price || 0,
            invoice_text: d.invoice_text || '',
            couverts_suggeres: d.couverts_suggeres || 0,
          });
        })
        .catch(() => { /* silencieux : le tarif reste sur la dernière valeur connue */ });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [computeBody, catalog, token]);

  // ── Compteur remise fidélité (propose, n'applique jamais) ────────
  const fidelityKey = JSON.stringify({
    to: form.tour_operator,
    date: form.booking_date,
    pax: parseInt(form.pax_count, 10) || 0,
    id: initialData?.id || 0,
  });
  useEffect(() => {
    const k = JSON.parse(fidelityKey);
    if (!k.to.trim() || !k.date || k.pax < 40) { setFidelity(null); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      const qs = `tour_operator=${encodeURIComponent(k.to)}&date=${k.date}&exclude_id=${k.id}`;
      fetch(`${API_BASE}/admin/group-pricing/fidelity?${qs}`, {
        headers: token ? { Authorization: 'Bearer ' + token } : {},
      })
        .then(r => (r.ok ? r.json() : Promise.reject(new Error())))
        .then(d => { if (alive) setFidelity(d); })
        .catch(() => { if (alive) setFidelity(null); });
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [fidelityKey, token]);

  // ── Etat des echanges Gmail (lecture seule) ─────────────────────
  const [emails, setEmails] = useState(null);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [openThread, setOpenThread] = useState(null);
  const [threadDetail, setThreadDetail] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [voirAutres, setVoirAutres] = useState(false);
  const [refCopiee, setRefCopiee] = useState(false);
  const [pinUrl, setPinUrl] = useState('');
  const [pinErr, setPinErr] = useState('');
  const bookingId = initialData?.id || null;

  useEffect(() => {
    if (!bookingId) return undefined;   // pas d'echanges sur une fiche pas encore creee
    let alive = true;
    setEmailsLoading(true);
    fetch(`${API_BASE}/admin/group-bookings/${bookingId}/emails`, {
      headers: token ? { Authorization: 'Bearer ' + token } : {},
    })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
      .then(d => { if (alive) setEmails(d); })
      .catch(() => { if (alive) setEmails({ threads: [], error: 'Impossible de joindre le serveur.' }); })
      .finally(() => { if (alive) setEmailsLoading(false); });
    return () => { alive = false; };
  }, [bookingId, token]);

  const rechargerEmails = () => {
    if (!bookingId) return;
    setEmailsLoading(true);
    fetch(`${API_BASE}/admin/group-bookings/${bookingId}/emails?refresh=1`, {
      headers: token ? { Authorization: 'Bearer ' + token } : {},
    })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error())))
      .then(d => setEmails(d))
      .catch(() => setEmails(e => e || { threads: [], error: 'Impossible de joindre le serveur.' }))
      .finally(() => setEmailsLoading(false));
  };

  const ouvrirThread = (id) => {
    if (openThread === id) { setOpenThread(null); setThreadDetail(null); return; }
    setOpenThread(id);
    setThreadDetail(null);
    setThreadLoading(true);
    fetch(`${API_BASE}/admin/group-bookings/${bookingId}/emails/${id}`, {
      headers: token ? { Authorization: 'Bearer ' + token } : {},
    })
      .then(r => (r.ok ? r.json() : r.json().then(e => Promise.reject(new Error(e.error || 'Erreur')))))
      .then(d => setThreadDetail(d))
      .catch(e => setThreadDetail({ error: e.message }))
      .finally(() => setThreadLoading(false));
  };

  const epingler = () => {
    const url = pinUrl.trim();
    if (!url) return;
    setPinErr('');
    fetch(`${API_BASE}/admin/group-bookings/${bookingId}/emails/pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
      body: JSON.stringify({ url }),
    })
      .then(r => (r.ok ? r.json() : r.json().then(e => Promise.reject(new Error(e.error || 'Erreur')))))
      .then(() => { setPinUrl(''); rechargerEmails(); })
      .catch(e => setPinErr(e.message));
  };

  const epinglerId = (id) => {
    fetch(`${API_BASE}/admin/group-bookings/${bookingId}/emails/pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
      body: JSON.stringify({ thread_id: id }),
    })
      .then(() => rechargerEmails())
      .catch(() => {});
  };

  const desepingler = (id) => {
    fetch(`${API_BASE}/admin/group-bookings/${bookingId}/emails/${id}/pin`, {
      method: 'DELETE',
      headers: token ? { Authorization: 'Bearer ' + token } : {},
    })
      .then(() => rechargerEmails())
      .catch(() => {});
  };

  const reference = referenceDossier(initialData?.id);
  const copierRef = () => {
    if (!reference) return;
    const fini = () => { setRefCopiee(true); setTimeout(() => setRefCopiee(false), 2200); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(reference).then(fini).catch(() => window.prompt('Copie manuelle :', reference));
    } else {
      window.prompt('Copie manuelle :', reference);
    }
  };

  const tousLesFils = emails ? (emails.threads || []) : [];
  const filsLies = tousLesFils.filter(t => t.related);
  const filsAutres = tousLesFils.filter(t => !t.related);

  const dateCourte = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const dateLongue = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  // Un message parti de chez nous s'affiche differemment des messages recus
  const deNous = (from) => /surabayacafe\.fr/i.test(String(from || ''));

  // ── Actions sur les lignes de prix ──────────────────────────────
  const setOverride = (key, patch) =>
    setOverrides(o => ({ ...o, [key]: { ...(o[key] || {}), ...patch } }));
  const clearOverride = (key) =>
    setOverrides(o => { const n = { ...o }; delete n[key]; return n; });

  const startEdit = (line) => {
    if (form.pricing_locked) return;
    setEditingKey(line.key);
    setEditValue(String(line.amount == null ? 0 : line.amount).replace('.', ','));
  };
  const commitEdit = (line) => {
    setOverride(line.key, { amount: parseEur(editValue) });
    setEditingKey(null);
  };

  const copyInvoice = () => {
    const txt = totals.invoice_text || '';
    if (!txt) return;
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2200); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(txt).then(done).catch(() => window.prompt('Copie manuelle :', txt));
    } else {
      window.prompt('Copie manuelle :', txt);
    }
  };

  const catByCat = (cat) => (catalog || []).filter(r => r.category === cat);
  const isFormuleJournee = catByCat('formule').some(r => r.code === form.formula_code);
  const couvertsSuggeres = (parseInt(form.pax_count, 10) || 0) + (parseInt(form.pax_free, 10) || 0);
  // La ligne « gratuités » seule ne fait pas un tarif : sans formule choisie
  // le bloc reste vide plutôt que d'afficher un total à 0 € (cf. brief A5).
  const aUnTarif = priceLines.some(l => l.key !== 'gratuites');

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateSeg = (i, patch) => setSegments(segs => segs.map((sg, idx) => idx === i ? { ...sg, ...patch } : sg));
  const updateSegDetail = (i, k, v) => setSegments(segs => segs.map((sg, idx) => idx === i ? { ...sg, details: { ...sg.details, [k]: v } } : sg));
  const addSeg = (type) => setSegments(segs => [...segs, blankSegment(type)]);
  const dupSeg = (i) => setSegments(segs => {
    const copy = segs.slice();
    copy.splice(i + 1, 0, JSON.parse(JSON.stringify(segs[i])));
    return copy;
  });
  const removeSeg = (i) => setSegments(segs => segs.filter((_, idx) => idx !== i));
  const moveSeg = (i, dir) => setSegments(segs => {
    const j = i + dir;
    if (j < 0 || j >= segs.length) return segs;
    const copy = segs.slice();
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });

  const openGmail = () => {
    if (!form.contact_email) return;
    const url = 'https://mail.google.com/mail/u/0/#search/' + encodeURIComponent(form.contact_email);
    window.open(url, '_blank', 'noopener');
  };

  // ── Garde anti-perte : fermer avec des modifs non sauvées demande confirmation ──
  const initialSnap = useRef(null);
  if (initialSnap.current === null) initialSnap.current = JSON.stringify({ form, segments, overrides });
  const isDirty = () => JSON.stringify({ form, segments, overrides }) !== initialSnap.current;
  const requestClose = () => {
    if (isDirty() && !window.confirm('Des modifications ne sont pas enregistrées. Fermer sans sauvegarder ?')) return;
    onClose();
  };
  // Ne ferme que si le clic a commencé ET fini sur le fond (pas de fermeture
  // quand on relâche hors de la fenêtre après une sélection de texte)
  const overlayMouseDown = useRef(false);
  const onOverlayMouseDown = (e) => { overlayMouseDown.current = e.target === e.currentTarget; };
  const onOverlayClick = (e) => {
    if (e.target !== e.currentTarget || !overlayMouseDown.current) return;
    requestClose();
  };

  const handleSubmit = async () => {
    if (!form.tour_operator.trim()) return setError('Le nom du TO / groupe est obligatoire');
    if (!form.booking_date) return setError('La date est obligatoire');
    setLoading(true);
    setError('');
    try {
      const nbGroups = parseInt(form.nb_groups) || 1;
      const payload = {
        ...form,
        pax_count: parseInt(form.pax_count) || 1,
        nb_groups: nbGroups,
        // ── Tarif : '' -> null, le backend recalcule et persiste ──
        formula_code: form.formula_code || null,
        menu_choice: form.formula_code && isFormuleJournee ? (form.menu_choice || null) : null,
        drink_package: form.drink_package || null,
        options: Array.isArray(form.options) ? form.options : [],
        pax_free: parseInt(form.pax_free, 10) || 0,
        discount_pct: parseFloat(form.discount_pct) || 0,
        bateau_non_facture: !!form.bateau_non_facture,
        pricing_locked: !!form.pricing_locked,
        price_lines: priceLines,
        segments: segments.map((sg, idx) => {
          // clamp : si pas de division, tout en commun ; sinon borne au nb de groupes
          let sub = parseInt(sg.sub_group) || 0;
          if (nbGroups <= 1 || sub > nbGroups) sub = 0;
          return {
            activity_type: sg.activity_type,
            start_time: sg.start_time || null,
            end_time: sg.end_time || null,
            sort_order: idx,
            sub_group: sub,
            details: sg.activity_type === 'repas'
              ? { ...sg.details, couverts: sg.details.couverts === '' || sg.details.couverts == null ? null : parseInt(sg.details.couverts) || null }
              : sg.details,
          };
        }),
      };
      if (isEdit) payload.id = initialData.id;
      await onSave(payload);
      onClose();
    } catch (e) {
      setError(e.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  // Rendu d'une ligne de fil. Extrait pour servir aux deux listes : ceux
  // qui parlent de cette journee, et les autres echanges du contact.
  const renderFil = (t) => (
      <div key={t.thread_id}>
        <div style={s.mailRow} onClick={() => ouvrirThread(t.thread_id)}>
          <div style={s.mailHead}>
            <span style={s.mailSubject}>
              {t.pinned && <span title="Fil épinglé à la main">📌 </span>}
              {t.subject}
            </span>
            <span style={s.mailMeta}>
              {t.message_count} msg{t.message_count > 1 ? 's' : ''} · {dateCourte(t.last_date)}
            </span>
            <span style={{ color: COLORS.gris }}>{openThread === t.thread_id ? '▾' : '▸'}</span>
          </div>
          {t.snippet && <div style={s.mailSnippet}>{t.snippet}</div>}
          {t.participants && t.participants.length > 0 && (
            <div style={{ ...s.mailMeta, marginTop: '4px', whiteSpace: 'normal' }}>{t.participants.join(' · ')}</div>
          )}
        </div>

        {openThread === t.thread_id && (
          <div style={{ margin: '0 0 14px 16px', paddingLeft: '14px', borderLeft: `2px solid ${COLORS.or}` }}>
            {threadLoading && <div style={{ fontSize: '13px', color: COLORS.gris }}>Ouverture du fil…</div>}
            {threadDetail && threadDetail.error && (
              <div style={{ fontSize: '13px', color: COLORS.rouge }}>⚠️ {threadDetail.error}</div>
            )}
            {threadDetail && !threadDetail.error && (threadDetail.messages || []).map(m => (
              <div key={m.id} style={{ ...s.bulle, ...(deNous(m.from) ? s.bulleNous : {}) }}>
                <div style={s.bulleHead}>
                  <span><strong>{m.from}</strong></span>
                  <span>{dateLongue(m.date)}</span>
                </div>
                <div style={s.bulleBody}>{m.body || '(message sans texte)'}</div>
                {(m.attachments || []).length > 0 && (
                  <div style={{ ...s.mailMeta, marginTop: '8px', whiteSpace: 'normal' }}>
                    📎 {m.attachments.map(a => `${a.filename} (${Math.round((a.size || 0) / 1024)} Ko)`).join(' · ')}
                  </div>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
              <a style={s.miniBtn} href={`https://mail.google.com/mail/u/0/#inbox/${t.thread_id}`}
                 target="_blank" rel="noopener noreferrer">↗️ Ouvrir dans Gmail</a>
              {t.pinned
                ? <button style={s.miniBtn} onClick={() => desepingler(t.thread_id)}>📌 Ne plus épingler</button>
                : <button style={s.miniBtn} onClick={() => epinglerId(t.thread_id)}>📌 Épingler</button>}
            </div>
          </div>
        )}
      </div>
  );

  return (
    <div style={s.overlay} onMouseDown={onOverlayMouseDown} onClick={onOverlayClick}>
      <div style={s.box} onClick={e => e.stopPropagation()}>
        <style>{`input:focus,select:focus,textarea:focus{border-color:${COLORS.or} !important;box-shadow:0 0 0 3px rgba(232,196,124,0.2)}`}</style>

        <div style={s.title}>{isEdit ? '✏️ Modifier la journée groupe' : '🚌 Nouvelle journée groupe'}</div>
        <div style={s.sub}>Constructeur à la carte — back-office uniquement</div>

        {/* Reference de dossier : sert a rattacher les mails a CETTE journee.
            Elle part deja dans le texte "Copier pour facture". */}
        {reference && (
          <div style={{ ...s.banner, backgroundColor: COLORS.grisClair, borderColor: COLORS.or, color: COLORS.brun, marginBottom: '18px' }}>
            <span>📁 Référence dossier <strong style={{ fontSize: '15px', letterSpacing: '0.5px' }}>{reference}</strong></span>
            <button style={s.miniBtn} onClick={copierRef}>{refCopiee ? '✅ Copiée' : '📋 Copier'}</button>
            <span style={{ fontSize: '11.5px', fontStyle: 'italic' }}>
              Demande au client de la rappeler dans ses mails : les fils qui la citent remontent tout seuls ci-dessous, même envoyés depuis une autre adresse.
            </span>
          </div>
        )}

        {error && <div style={s.errorBox}>⚠️ {error}</div>}

        {/* ── Bloc contact ── */}
        <div style={{ ...s.sectionTitle, marginTop: 0 }}>👤 Contact</div>
        <div style={s.row}>
          <div style={s.group}>
            <label style={s.label}>TO / Groupe *</label>
            <input style={s.input} value={form.tour_operator} onChange={e => setF('tour_operator', e.target.value)} placeholder="Ex : Voyages Dupont" />
          </div>
          <div style={s.group}>
            <label style={s.label}>Nom du contact</label>
            <input style={s.input} value={form.contact_name} onChange={e => setF('contact_name', e.target.value)} placeholder="Personne à contacter" />
          </div>
        </div>
        <div style={s.row}>
          <div style={s.group}>
            <label style={s.label}>Email</label>
            <input style={s.input} type="email" value={form.contact_email} onChange={e => setF('contact_email', e.target.value)} placeholder="contact@to.com" />
          </div>
          <div style={s.group}>
            <label style={s.label}>Téléphone</label>
            <input style={s.input} value={form.contact_phone} onChange={e => setF('contact_phone', e.target.value)} placeholder="06 12 34 56 78" />
          </div>
        </div>
        <div style={s.row3}>
          <div style={s.group}>
            <label style={s.label}>Date *</label>
            <input style={s.input} type="date" value={form.booking_date} onChange={e => setF('booking_date', e.target.value)} />
          </div>
          <div style={s.group}>
            <label style={s.label}>Nb pax</label>
            <input style={s.input} type="number" min="1" max="200" value={form.pax_count} onChange={e => setF('pax_count', e.target.value === '' ? '' : parseInt(e.target.value) || 1)} onBlur={e => { if (!e.target.value || e.target.value < 1) setF('pax_count', 1); }} />
          </div>
          <div style={s.group}>
            <label style={s.label}>Statut</label>
            <select style={s.input} value={form.status} onChange={e => setF('status', e.target.value)}>
              {Object.entries(STATUS_OPTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>

        {/* ── Division en sous-groupes ── */}
        <div style={{ ...s.group, marginTop: '4px' }}>
          <label style={s.label}>Diviser le groupe</label>
          <select style={{ ...s.input, maxWidth: '320px' }} value={form.nb_groups} onChange={e => setF('nb_groups', parseInt(e.target.value))}>
            <option value={1}>Non — un seul groupe</option>
            <option value={2}>En 2 sous-groupes (G1 / G2)</option>
            <option value={3}>En 3 sous-groupes (G1 / G2 / G3)</option>
          </select>
          {form.nb_groups > 1 && (
            <div style={{ fontSize: '12px', color: COLORS.gris, fontStyle: 'italic', marginTop: '6px' }}>
              Affecte chaque activité à un sous-groupe (dégust/calèche en décalé) ou laisse en « Commun » (repas/bateau ensemble). Bouton ⧉ pour dupliquer une activité d'un groupe à l'autre.
            </div>
          )}
        </div>

        {/* ── Bloc programme ── */}
        <div style={s.sectionTitle}>🧩 Programme de la journée</div>
        {segments.map((seg, i) => {
          const a = ACTIVITIES[seg.activity_type];
          return (
            <div key={i} style={s.segCard}>
              <div style={s.segHead}>
                <div style={s.segTitle}>
                  {a.icon} {a.label}
                  {form.nb_groups > 1 && (
                    <span style={{ fontSize: '11px', fontWeight: 'bold', padding: '2px 8px', borderRadius: '20px', backgroundColor: (seg.sub_group >= 1 ? '#FCEFC7' : COLORS.grisClair), color: (seg.sub_group >= 1 ? '#7B5E00' : COLORS.gris) }}>
                      {seg.sub_group >= 1 ? `Groupe ${seg.sub_group}` : 'Commun'}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button style={s.iconBtn} title="Monter" onClick={() => moveSeg(i, -1)} disabled={i === 0}>↑</button>
                  <button style={s.iconBtn} title="Descendre" onClick={() => moveSeg(i, 1)} disabled={i === segments.length - 1}>↓</button>
                  <button style={s.iconBtn} title="Dupliquer" onClick={() => dupSeg(i)}>⧉</button>
                  <button style={{ ...s.iconBtn, borderColor: COLORS.rouge, color: COLORS.rouge }} title="Supprimer" onClick={() => removeSeg(i)}>✕</button>
                </div>
              </div>

              <div style={form.nb_groups > 1 ? s.row3 : s.row}>
                <div style={s.group}>
                  <label style={s.label}>Début</label>
                  <input style={s.input} type="time" value={seg.start_time || ''} onChange={e => updateSeg(i, { start_time: e.target.value })} />
                </div>
                <div style={s.group}>
                  <label style={s.label}>Fin</label>
                  <input style={s.input} type="time" value={seg.end_time || ''} onChange={e => updateSeg(i, { end_time: e.target.value })} />
                </div>
                {form.nb_groups > 1 && (
                  <div style={s.group}>
                    <label style={s.label}>Sous-groupe</label>
                    <select style={s.input} value={(seg.sub_group || 0) > form.nb_groups ? 0 : (seg.sub_group || 0)} onChange={e => updateSeg(i, { sub_group: parseInt(e.target.value) })}>
                      <option value={0}>Commun (tous)</option>
                      {Array.from({ length: form.nb_groups }, (_, n) => n + 1).map(n => (
                        <option key={n} value={n}>Groupe {n}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {seg.activity_type === 'accueil_degustation' && (
                <div style={s.group}>
                  <label style={s.label}>Remarque</label>
                  <input style={s.input} value={seg.details.remarque || ''} onChange={e => updateSegDetail(i, 'remarque', e.target.value)} placeholder="Ex : dégustation Sparrow IPA + planche" />
                </div>
              )}

              {seg.activity_type === 'caleche' && (
                <div style={s.group}>
                  <label style={s.label}>Destination</label>
                  <select style={s.input} value={seg.details.destination || 'belvedere'} onChange={e => updateSegDetail(i, 'destination', e.target.value)}>
                    {Object.entries(DESTINATIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              )}

              {seg.activity_type === 'bateau' && (
                <div style={s.group}>
                  <label style={s.label}>Prestataire</label>
                  <select style={s.input} value={seg.details.prestataire || 'nlb_les_brenets'} onChange={e => updateSegDetail(i, 'prestataire', e.target.value)}>
                    {Object.entries(PRESTATAIRES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              )}

              {seg.activity_type === 'repas' && (
                <>
                  <div style={s.row3}>
                    <div style={s.group}>
                      <label style={s.label}>Entrée</label>
                      <input style={s.input} value={seg.details.entree || ''} onChange={e => updateSegDetail(i, 'entree', e.target.value)} placeholder="texte libre" />
                    </div>
                    <div style={s.group}>
                      <label style={s.label}>Plat</label>
                      <input style={s.input} value={seg.details.plat || ''} onChange={e => updateSegDetail(i, 'plat', e.target.value)} placeholder="texte libre" />
                    </div>
                    <div style={s.group}>
                      <label style={s.label}>Dessert</label>
                      <input style={s.input} value={seg.details.dessert || ''} onChange={e => updateSegDetail(i, 'dessert', e.target.value)} placeholder="texte libre" />
                    </div>
                  </div>
                  <div style={s.row}>
                    <div style={s.group}>
                      <label style={s.label}>Couverts</label>
                      <input style={s.input} type="number" min="0" value={seg.details.couverts || ''} onChange={e => updateSegDetail(i, 'couverts', e.target.value)} placeholder={String(couvertsSuggeres)} />
                      {/* Payants + gratuités : le chef doit sortir les assiettes
                          du chauffeur et de l'accompagnateur. Proposé, pas imposé. */}
                      {couvertsSuggeres > 0 && parseInt(seg.details.couverts, 10) !== couvertsSuggeres && (
                        <div style={s.hint}>
                          <span>
                            {parseInt(form.pax_count, 10) || 0} payants + {parseInt(form.pax_free, 10) || 0} gratuité{(parseInt(form.pax_free, 10) || 0) > 1 ? 's' : ''} = <strong>{couvertsSuggeres} couverts</strong>
                          </span>
                          <button style={s.miniBtn} onClick={() => updateSegDetail(i, 'couverts', couvertsSuggeres)}>
                            Appliquer
                          </button>
                        </div>
                      )}
                    </div>
                    <div style={s.group}>
                      <label style={s.label}>Exceptions</label>
                      <input style={s.input} value={seg.details.exceptions || ''} onChange={e => updateSegDetail(i, 'exceptions', e.target.value)} placeholder="2 végé, 1 sans gluten…" />
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}

        <div style={s.addBar}>
          {ACTIVITY_ORDER.map(type => (
            <button key={type} style={s.addBtn} onClick={() => addSeg(type)}>
              + {ACTIVITIES[type].icon} {ACTIVITIES[type].label}
            </button>
          ))}
        </div>

        {/* ── Bloc tarif ── */}
        <div style={s.sectionTitle}>💶 Tarif</div>

        {catalogErr && (
          <div style={{ ...s.banner, backgroundColor: '#FFF0F0', borderColor: '#FFCCCC', color: COLORS.rouge }}>
            ⚠️ {catalogErr}
          </div>
        )}

        {form.pricing_locked && (
          <div style={{ ...s.banner, ...s.bannerLock }}>
            🔒 Tarif verrouillé — plus aucun recalcul automatique. Les montants restent tels quels même si tu changes la formule ou le nombre de pax.
          </div>
        )}

        {/* Remise fidélité : on propose, Mamat décide. Jamais automatique. */}
        {!form.pricing_locked && fidelity && fidelity.eligible && parseFloat(form.discount_pct) === 0 && (
          <div style={s.banner}>
            <span>
              🎁 {fidelity.count + 1}<sup>e</sup> groupe de 40 pax ou plus de <strong>{form.tour_operator}</strong> cette saison — appliquer la remise fidélité 10 % ?
            </span>
            <button style={s.miniBtn} onClick={() => setF('discount_pct', 10)}>Oui, 10 %</button>
          </div>
        )}

        <div style={s.tarifGrid}>
          <div style={s.group}>
            <label style={s.label}>Formule</label>
            <select
              style={s.input}
              value={form.formula_code}
              disabled={form.pricing_locked}
              onChange={e => {
                const code = e.target.value;
                // un menu n'a de sens que sur une formule journée
                const estJournee = catByCat('formule').some(r => r.code === code);
                setForm(f => ({
                  ...f,
                  formula_code: code,
                  menu_choice: estJournee ? (f.menu_choice || 'menu23') : '',
                  bateau_non_facture: code === 'navigation' ? f.bateau_non_facture : false,
                }));
              }}
            >
              <option value="">— Aucun tarif —</option>
              {catByCat('formule').length > 0 && (
                <optgroup label="Journée (calèche + repas)">
                  {catByCat('formule').map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
                </optgroup>
              )}
              {catByCat('prestation_seche').length > 0 && (
                <optgroup label="Prestation sèche (sans repas)">
                  {catByCat('prestation_seche').map(r => (
                    <option key={r.code} value={r.code}>{r.label} — {fmtEur(r.price)}</option>
                  ))}
                </optgroup>
              )}
              <option value="sur_mesure">Sur mesure (tout à la main)</option>
            </select>
          </div>

          {isFormuleJournee && (
            <div style={s.group}>
              <label style={s.label}>Menu</label>
              <select style={s.input} value={form.menu_choice || 'menu23'} disabled={form.pricing_locked}
                onChange={e => setF('menu_choice', e.target.value)}>
                {Object.entries(MENU_OPTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          )}

          <div style={s.group}>
            <label style={s.label}>Forfait boisson</label>
            <select style={s.input} value={form.drink_package} disabled={form.pricing_locked}
              onChange={e => setF('drink_package', e.target.value)}>
              <option value="">— Aucun —</option>
              {catByCat('forfait_boisson').map(r => (
                <option key={r.code} value={r.code}>{r.label} — {fmtEur(r.price)}</option>
              ))}
            </select>
          </div>

          <div style={s.group}>
            <label style={s.label}>Gratuités</label>
            <input style={s.input} type="number" min="0" max="20" value={form.pax_free}
              disabled={form.pricing_locked}
              onChange={e => setF('pax_free', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
              onBlur={e => { if (e.target.value === '') setF('pax_free', 0); }} />
            <div style={s.hint}>Chauffeur + accompagnateur. Facturés 0 €, comptés dans les couverts.</div>
          </div>

          <div style={s.group}>
            <label style={s.label}>Remise %</label>
            <input style={s.input} type="number" min="0" max="100" step="0.5" value={form.discount_pct}
              disabled={form.pricing_locked}
              onChange={e => setF('discount_pct', e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)} />
          </div>
        </div>

        {catByCat('option').length > 0 && (
          <div style={{ ...s.group, marginTop: '6px' }}>
            <label style={s.label}>Options</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px' }}>
              {catByCat('option').map(r => (
                <label key={r.code} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13.5px', cursor: form.pricing_locked ? 'not-allowed' : 'pointer' }}>
                  <input
                    type="checkbox"
                    disabled={form.pricing_locked}
                    checked={form.options.includes(r.code)}
                    onChange={e => setF('options', e.target.checked
                      ? [...form.options, r.code]
                      : form.options.filter(c => c !== r.code))}
                  />
                  {r.label} <span style={{ color: COLORS.gris }}>({fmtEur(r.price)})</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Navigation impossible : la formule vendue reste Navigation,
            on déduit la part bateau et le motif apparaît sur la facture. */}
        {form.formula_code === 'navigation' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px', marginTop: '10px', cursor: form.pricing_locked ? 'not-allowed' : 'pointer' }}>
            <input type="checkbox" disabled={form.pricing_locked} checked={!!form.bateau_non_facture}
              onChange={e => setF('bateau_non_facture', e.target.checked)} />
            ⛵ Bateau non facturé (navigation impossible)
          </label>
        )}

        {/* ── Lignes de prix ── */}
        {aUnTarif ? (
          <div style={s.tarifTable}>
            {priceLines.map(l => {
              const modifiee = !!l.override;
              return (
                <div key={l.key} style={{ ...s.tarifRow, backgroundColor: l.informative ? '#FAF7F2' : COLORS.blanc }}>
                  <div style={{ flex: 1, minWidth: 0, color: l.informative ? COLORS.gris : COLORS.brun, fontStyle: l.informative ? 'italic' : 'normal' }}>
                    {modifiee && <span title={l.note || 'Montant saisi à la main'}>✏️ </span>}
                    {l.label}
                    {l.informative && <span style={{ fontSize: '11px' }}> — déjà inclus, non compté</span>}
                    {l.note && <div style={{ fontSize: '11px', color: COLORS.gris }}>{l.note}</div>}
                  </div>
                  <div style={s.tarifDetail}>
                    {l.unit != null && l.qty != null ? `${fmtEur(l.unit)} × ${l.qty}` : (l.pct != null ? `${l.pct} %` : '')}
                  </div>
                  {editingKey === l.key ? (
                    <input
                      style={s.tarifInput}
                      autoFocus
                      value={editValue}
                      onChange={e => setEditValue(e.target.value)}
                      onBlur={() => commitEdit(l)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') commitEdit(l);
                        if (e.key === 'Escape') setEditingKey(null);
                      }}
                    />
                  ) : (
                    <div
                      style={{ ...s.tarifAmount, borderColor: modifiee ? COLORS.or : 'transparent', color: (parseFloat(l.amount) || 0) < 0 ? COLORS.rouge : COLORS.brun, cursor: form.pricing_locked ? 'default' : 'pointer' }}
                      title={form.pricing_locked ? 'Tarif verrouillé' : 'Cliquer pour saisir le montant à la main'}
                      onClick={() => startEdit(l)}
                    >
                      {fmtEur(l.amount)}
                    </div>
                  )}
                  <button
                    style={{ ...s.iconBtn, visibility: modifiee && !form.pricing_locked ? 'visible' : 'hidden' }}
                    title="Recalculer cette ligne"
                    onClick={() => clearOverride(l.key)}
                  >↺</button>
                </div>
              );
            })}

            <div style={s.tarifFoot}>
              <div>
                <div style={s.tarifBigLabel}>Prix / personne payante</div>
                <div style={s.tarifBig}>{fmtEur(totals.unit_price)}</div>
              </div>
              <div>
                <div style={s.tarifBigLabel}>Total TTC</div>
                <div style={s.tarifBig}>{fmtEur(totals.total_price)}</div>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ ...s.recap, marginTop: '14px' }}>
            {catalog === null ? 'Chargement du catalogue…' : 'Aucun tarif renseigné. Choisis une formule ci-dessus.'}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
          <button style={s.miniBtn} onClick={() => setF('pricing_locked', !form.pricing_locked)}>
            {form.pricing_locked ? '🔓 Déverrouiller le tarif' : '🔒 Verrouiller le tarif'}
          </button>
          <button
            style={{ ...s.miniBtn, opacity: aUnTarif ? 1 : 0.4, cursor: aUnTarif ? 'pointer' : 'not-allowed' }}
            disabled={!aUnTarif || !totals.invoice_text}
            onClick={copyInvoice}
            title="Texte plat, collable direct dans un devis ou Pennylane"
          >
            {copied ? '✅ Copié' : '📋 Copier pour facture'}
          </button>
        </div>

        {/* ── Notes internes ── */}
        <div style={s.sectionTitle}>🗒️ Notes / commentaires</div>
        <div style={s.group}>
          <textarea
            style={{ ...s.input, minHeight: '72px', resize: 'vertical' }}
            value={form.notes}
            onChange={e => setF('notes', e.target.value)}
            placeholder="Notes internes : facturation, sono, allergies, demandes particulières…"
          />
        </div>

        {/* ── Récap ── */}
        <div style={s.sectionTitle}>📋 Récap de la journée</div>
        <div style={s.recap}>{buildRecap(segments, form.nb_groups)}</div>

        {/* ── Echanges Gmail (lecture seule) ── */}
        <div style={s.sectionTitle}>
          ✉️ Échanges
          {tousLesFils.length > 0 && (
            <span style={{ fontWeight: 'normal', textTransform: 'none', letterSpacing: 0 }}>
              {' — '}{filsLies.length} lié{filsLies.length > 1 ? 's' : ''} à cette journée
              {filsAutres.length > 0 && ` · ${filsAutres.length} autre${filsAutres.length > 1 ? 's' : ''}`}
            </span>
          )}
        </div>

        {!bookingId ? (
          <div style={s.recap}>Crée d'abord la journée : les échanges se chargent depuis la fiche enregistrée.</div>
        ) : (
          <>
            {emails && emails.error && (
              <div style={{ ...s.banner, backgroundColor: '#FFF0F0', borderColor: '#FFCCCC', color: COLORS.rouge }}>
                ⚠️ {emails.error}
              </div>
            )}

            {emailsLoading && !emails && <div style={s.recap}>Chargement des échanges…</div>}

            {emails && (emails.threads || []).length === 0 && !emailsLoading && (
              <div style={s.recap}>
                Aucun fil trouvé{form.contact_email ? ` pour ${form.contact_email}` : ' (pas d\'email renseigné pour ce contact)'}.
                {form.contact_email && (
                  <div style={{ marginTop: '10px' }}>
                    <button style={s.miniBtn} onClick={openGmail}>🔍 Chercher dans Gmail</button>
                  </div>
                )}
              </div>
            )}

            {filsLies.length === 0 && filsAutres.length > 0 && (
              <div style={s.recap}>
                Aucun fil ne cite la référence <strong>{reference}</strong> ni la date du {new Date(form.booking_date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}.
                Ouvre « Autres échanges » ci-dessous et épingle ceux qui concernent cette journée.
              </div>
            )}

            {filsLies.map(renderFil)}

            {filsAutres.length > 0 && (
              <div style={{ marginTop: filsLies.length ? '14px' : 0 }}>
                <button
                  style={{ ...s.miniBtn, width: '100%', textAlign: 'left', backgroundColor: COLORS.grisClair, fontWeight: 'normal' }}
                  onClick={() => setVoirAutres(v => !v)}
                >
                  {voirAutres ? '▾' : '▸'} Autres échanges avec ce contact ({filsAutres.length}) — aucun lien détecté avec cette journée
                </button>
                {voirAutres && <div style={{ marginTop: '10px' }}>{filsAutres.map(renderFil)}</div>}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                style={{ ...s.input, flex: 1, minWidth: '220px' }}
                value={pinUrl}
                onChange={e => setPinUrl(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); epingler(); } }}
                placeholder="+ Ajouter un fil par URL Gmail (mail arrivé sur une autre adresse)"
              />
              <button style={s.miniBtn} onClick={epingler} disabled={!pinUrl.trim()}>📌 Épingler</button>
              <button style={s.miniBtn} onClick={rechargerEmails} disabled={emailsLoading} title="Forcer la relecture de Gmail">
                {emailsLoading ? '⏳' : '↻'}
              </button>
            </div>
            {pinErr && <div style={{ ...s.hint, color: COLORS.rouge }}>⚠️ {pinErr}</div>}
            {emails && emails.source === 'cache' && (emails.threads || []).length > 0 && (
              <div style={s.hint}>Fils déjà connus, affichés depuis la base — Gmail n'a pas répondu.</div>
            )}
          </>
        )}

        {/* ── Actions ── */}
        <div style={s.actions}>
          {isEdit && onDelete && (
            confirmDelete ? (
              <>
                <span style={{ fontSize: '13px', color: COLORS.rouge, alignSelf: 'center' }}>Supprimer définitivement ?</span>
                <button style={s.btnSecondary} onClick={() => setConfirmDelete(false)}>Non</button>
                <button style={{ ...s.btnPrimary, backgroundColor: COLORS.rouge, color: '#fff' }} onClick={() => onDelete(initialData.id)}>Oui, supprimer</button>
              </>
            ) : (
              <button style={{ ...s.btnSecondary, borderColor: COLORS.rouge, color: COLORS.rouge }} onClick={() => setConfirmDelete(true)}>🗑️ Supprimer</button>
            )
          )}
          <div style={{ flex: 1 }} />
          <button style={s.btnSecondary} onClick={requestClose}>Annuler</button>
          <button style={{ ...s.btnPrimary, opacity: loading ? 0.7 : 1 }} onClick={handleSubmit} disabled={loading}>
            {loading ? '⏳ Enregistrement…' : (isEdit ? '✅ Enregistrer' : '✅ Créer la journée')}
          </button>
        </div>
      </div>
    </div>
  );
}
